import { Icons, Stack, styled } from '@/theme/index';
import { Nullable } from '@/utils/helperTypes';

export type ReportMonitor = {
  created_at: string;
  id: number;
  is_claimed: boolean;
  next_run_at: string;
  permalink: string;
  report_type: string;
  search_data?: ReportSearchData;
  update_frequency: number;
  updated_at: string;
  user_code: string;
} & Nullable<{
  discarded_at: string;
}>;

export type PersonReportSearchData = {
  age: number;
  city: string;
  first_name: string;
  full_size_image?: string;
  last_name: string;
  middle_name: string;
  state: string;
  thumb?: string;
};

export type PhoneReportSearchData = {
  phone_number: string;
} & Nullable<{
  city: string;
  first_name: string;
  last_name: string;
  middle_name: string;
  state: string;
}>;

export type PropertyReportSearchData = {
  address: string;
  city?: string;
  state?: string;
  zip_code?: string;
};

export type SocialNetworkReportSearchData = {
  email: string;
  thumb?: string;
  full_size_image?: string;
};

export type VehicleReportSearchData = {
  make: string;
  model: string;
  state: string;
  year: number;
} & Nullable<{
  license_plate: string;
  vin: string;
}>;

export type UsernameReportSearchData = {
  username: string;
};

export type ContactReportSearchData = {
  first_name: string;
  last_name: string;
  middle_name: string;
  phone_number: string;
  email: string;
} & Nullable<{
  city: string;
  state: string;
}>;

export type ReportSearchData =
  | PersonReportSearchData
  | PhoneReportSearchData
  | PropertyReportSearchData
  | SocialNetworkReportSearchData
  | VehicleReportSearchData
  | UsernameReportSearchData
  | ContactReportSearchData;

export function isPersonReportSearchData(
  value: ReportSearchData
): value is PersonReportSearchData {
  return Object.prototype.hasOwnProperty.call(value, 'age');
}

export function isPhoneReportSearchData(
  value: ReportSearchData
): value is PhoneReportSearchData {
  return Object.prototype.hasOwnProperty.call(value, 'phone_number');
}

export function isPropertyReportSearchData(
  value: ReportSearchData
): value is PropertyReportSearchData {
  return Object.prototype.hasOwnProperty.call(value, 'zip_code');
}

export function isSocialNetworkReportSearchData(
  value: ReportSearchData
): value is SocialNetworkReportSearchData {
  return Object.prototype.hasOwnProperty.call(value, 'email');
}

export function isVehicleReportSearchData(
  value: ReportSearchData
): value is VehicleReportSearchData {
  return Object.prototype.hasOwnProperty.call(value, 'make');
}

export function isUsernameReportSearchData(
  value: ReportSearchData
): value is UsernameReportSearchData {
  return Object.prototype.hasOwnProperty.call(value, 'username');
}

export function isContactReportSearchData(
  value: ReportSearchData
): value is ContactReportSearchData {
  return (
    Object.prototype.hasOwnProperty.call(value, 'first_name') &&
    Object.prototype.hasOwnProperty.call(value, 'last_name')
  );
}

interface ReportType {
  [key: string]: [string, JSX.Element];
}

export const reportTypes: ReportType = {
  reverse_phone_report: [
    'Phone Monitor',
    <Icons.PhoneRounded
      fontSize="large"
      color="primary"
      sx={{ marginRight: 2 }}
    />,
  ],
  property_report: [
    'Property Monitor',
    <Icons.HomeRounded
      fontSize="large"
      color="primary"
      sx={{ marginRight: 2 }}
    />,
  ],
  detailed_person_report: [
    'Person Monitor',
    <Icons.PersonRounded
      fontSize="large"
      color="primary"
      sx={{ marginRight: 2 }}
    />,
  ],
  social_network_report: [
    'Email Monitor',
    <Icons.EmailRounded
      fontSize="large"
      color="primary"
      sx={{ marginRight: 2 }}
    />,
  ],
  vehicle_report: [
    'Vehicle Monitor',
    <Icons.DirectionsCar
      fontSize="large"
      color="primary"
      sx={{ marginRight: 2 }}
    />,
  ],
  contact_report: [
    'Contact Pro Monitor',
    <Icons.BusinessRounded
      fontSize="large"
      color="primary"
      sx={{ marginRight: 2 }}
    />,
  ],
  null: [
    'Person Monitor',
    <Icons.PersonRounded
      fontSize="large"
      color="primary"
      sx={{ marginRight: 2 }}
    />,
  ],
};

export const MonitorItem = styled(Stack)(({ theme }) => ({
  marginTop: theme.spacing(2),
  width: '100%',
}));

export const MonitorsContainer = styled('div')(({ theme }) => ({
  maxHeight: theme.spacing(50),
}));
